import { _ } from "../../core/polyglot/polyglot.js";
import AccountGroupRowTemplateCreator from "./templates/rowTemplates/AccountGroupRowTemplateCreator.js";
import PreliminaryResultRowTemplate from "./templates/rowTemplates/PreliminaryResultRowTemplate.js";
import Report from "./reports/Report.js";

/**
 * `TemplateInterpreter` creates a report by interpreting a report
 * template and its items. Interpretation is configured through an
 * interpretation context.
 *
 * All item templates (rows and columns) of the report template
 * are recursively interpreted to create an instance counter-part
 * for each one of them.
 *
 * Some item templates are not in the report template though. This
 * is for example the case of the sub-rows of account group rows
 * as they are generated dynamicaly based on the chart of accounts
 * of the interpretation context. This means that interpreting a
 * report template might result in a report with more rows than in
 * the template itself.
 *
 * Interpreting a template several times with the same interpreter
 * (and thus same context) always results in the same
 * instance. This property is important for speed reasons and also
 * because of references between items.
 *
 * @param {interpretationContext} spec.context - The interpretation
 * context used to configure the interpretation.
 */
export default class TemplateInterpreter extends Object {
	constructor({ context } = {}) {
		super(...arguments);
		this._context = context;
		this._cache = new Map();
		this._reportTemplate = null;
	}

	getContext() {
		return this._context;
	}

	/**
	 * Return a new report based on `reportTemplate` and the
	 * interpretation context.
	 */
	interpret(reportTemplate) {
		this._reportTemplate = reportTemplate;

		const rows = this.interpretItemTemplates(reportTemplate.getRows());
		let columns = this.interpretItemTemplates(reportTemplate.getColumns());

		const extraColumns = this._interpretItemTemplatesOverRepeatingPeriods(
			reportTemplate.getColumns(),
		);
		columns = columns.concat(extraColumns.flat());

		if (this._reportTemplate.isBalanceSheet()) {
			this._injectPreliminaryResultRows(rows);
		}

		return new Report({
			title: reportTemplate.getTitle(),
			description: reportTemplate.getDescription(),
			template: reportTemplate,
			rows,
			columns,
			period: this._context.getPeriod(),
			chartOfAccounts: this._context.getChartOfAccounts(),
			dimensionSpecification: this._context.getDimensionSpecification(),
		});
	}

	/**
	 * Return a report item after interpreting the `item`
	 * template. Result is cached so interpreting twice the same
	 * item template returns the same instance.
	 */
	interpretItemTemplate(item) {
		if (!this._cache.get(item)) {
			this._cache.set(item, item.instantiate(this));
		}

		return this._cache.get(item);
	}

	interpretItemTemplates(items) {
		return items.map(this.interpretItemTemplate.bind(this));
	}

	/**
	 * Generate sub rows for the `parent` template row and its
	 * `accountGroup`.
	 *
	 * @param {AbstractAccountGroupRowTemplate} parent - The row
	 * template for which to generate sub rows.
	 * @param {AbstractAccountGroup} accountGroup - The chart of
	 * accounts group associated with `parent`.
	 */
	generateAccountGroupRows({ parent, accountGroup }) {
		const visitor = new AccountGroupRowTemplateCreator({
			accounts: this._context.getAccounts(),
			parent,
		});

		const generatedRowTemplates = accountGroup.accept(visitor);

		return this.interpretItemTemplates(generatedRowTemplates);
	}

	_interpretItemTemplatesOverRepeatingPeriods(items) {
		return this._context
			.getRepeatingPeriods()
			.slice()
			.map((period) => {
				const context = this._context.clone({ period });
				const interpreter = new this.constructor({ context });
				return interpreter.interpretItemTemplates(items);
			});
	}

	/**
	 * If the report template is a balanche sheet, injects
	 * preliminary result rows (for the current and previous
	 * years) in the correct group if it exists.
	 *
	 * Mutates `rows` in place.
	 */
	_injectPreliminaryResultRows(rows) {
		const groupRow = this._findRowForPreliminaryResult(rows);
		if (groupRow) {
			const previousPreliminaryResultRow = this._makePreliminaryResultRow(
				{
					parentRow: groupRow,
					keyFigureId:
						this._previousPreliminaryResultIdFromGroupRow(groupRow),
					title: _("Retained earnings"),
				},
			);
			const preliminaryResultRow = this._makePreliminaryResultRow({
				parentRow: groupRow,
				keyFigureId: this._preliminaryResultIdFromGroupRow(groupRow),
				title: _("Net profit"),
			});

			groupRow
				.getRows()
				.push(previousPreliminaryResultRow, preliminaryResultRow);

			this._flushAllRowsCache(groupRow);
		}
	}

	_preliminaryResultIdFromGroupRow(groupRow) {
		if (groupRow.isNegated()) {
			return "PreliminaryResult";
		}

		return "NegatedPreliminaryResult";
	}

	_previousPreliminaryResultIdFromGroupRow(groupRow) {
		if (groupRow.isNegated()) {
			return "PreviousPreliminaryResult";
		}

		return "NegatedPreviousPreliminaryResult";
	}

	// Reports and their rows and columns have been designed to be
	// immutable. In our implementation of preliminary row injection,
	// we mute rows which breaks the design. Because of this, we have
	// to flush getAllRows()'s cache
	_flushAllRowsCache(row) {
		row.flushAllRowsCache();

		if (row.getParent && row.getParent()) {
			this._flushAllRowsCache(row.getParent());
		}
	}

	/**
	 * Return the account group row where the preliminary result
	 * rows should be injected.
	 *
	 * If the account group ID is specified in the report template (with the
	 * attribute `accountGroupIdForPreliminaryResult`), lookup row representing
	 * the account group with that ID.
	 *
	 * If not specified in the report template, or if no account group can be
	 * found with that row, return the account group row for
	 * "NonRestrictedEquityCapital", otherwise fallback to
	 * "EquityCapital".
	 */
	_findRowForPreliminaryResult(rows) {
		const accountGroupRows = this._getAllAccountGroupRows(rows);
		const coa = this._context.getChartOfAccounts();

		const templateSpecifiedRow =
			this._findRowForPreliminaryResultFromTemplate({
				accountGroupRows,
				coa,
			});

		const equityCapitalRow = this._findRowRepresentingAccountGroup({
			rows: accountGroupRows,
			accountGroup: coa.findById("EquityCapital"),
		});

		const nonRestrictedEquityCapitalRow =
			this._findRowRepresentingAccountGroup({
				rows: accountGroupRows,
				accountGroup: coa.findById("NonRestrictedEquityCapital"),
			});

		return (
			templateSpecifiedRow ||
			nonRestrictedEquityCapitalRow ||
			equityCapitalRow
		);
	}

	_findRowForPreliminaryResultFromTemplate({ accountGroupRows, coa }) {
		const accountGroupId =
			this._reportTemplate.getAccountGroupIdForPreliminaryResult();

		if (!accountGroupId) {
			return undefined;
		}

		return this._findRowRepresentingAccountGroup({
			rows: accountGroupRows,
			accountGroup: coa.findById(accountGroupId),
		});
	}

	_findRowRepresentingAccountGroup({ rows, accountGroup }) {
		if (!accountGroup) {
			return undefined;
		}

		return rows.find((row) =>
			accountGroup.representsGroup(row.getAccountGroup()),
		);
	}

	_makePreliminaryResultRow({ parentRow, keyFigureId, title }) {
		const rowTemplate = new PreliminaryResultRowTemplate({
			id: `${parentRow.getStableId()}-preliminary-result-${keyFigureId}`,
			keyFigureId,
			title,
		});

		const row = this.interpretItemTemplate(rowTemplate);

		row.setParent(parentRow);

		return row;
	}

	_getAllAccountGroupRows(rows) {
		const allRows = rows.slice();
		rows.forEach((row) => allRows.push(...row.getAllRows()));
		return allRows.filter((row) => row.isAccountGroupRow());
	}
}
